import {
	type MfaChallengeState,
	postAuthGeneratePasskeyStepUpOptions,
	postAuthSendTwoFactorOtp,
	postAuthVerifyBackupCode,
	postAuthVerifyTotp,
	postAuthVerifyTwoFactorOtp,
	postAuthVerifyTwoFactorPasskey
} from '@repo/api-portal-sdk/gen'
import { Button } from '@repo/ui/button'
import { Input } from '@repo/ui/input'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { OtpInput } from '@repo/ui/modules/auth/components/otp-input'
import { TwoFactorDevicePicker } from '@repo/ui/modules/auth/components/two-factor-device-picker'
import { getPasskeyAssertion } from '@repo/ui/modules/auth/lib/webauthn'
import { Spinner } from '@repo/ui/spinner'
import { useMutation } from '@tanstack/react-query'
import {
	Fingerprint,
	KeyRound,
	Mail,
	ShieldCheck,
	Smartphone
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from '@/lib/i18n'

export function MfaChallenge(props: {
	mfa: MfaChallengeState
	onSuccess: () => Promise<void>
}) {
	const { t } = useTranslation()
	const seenMethodTypes = new Set<string>()
	const supportedMethods = props.mfa.methods.filter((method) => {
		if (
			!['totp', 'passkey', 'email_otp', 'recovery_code'].includes(
				method.type
			) ||
			seenMethodTypes.has(method.type)
		) {
			return false
		}

		seenMethodTypes.add(method.type)
		return true
	})
	const [method, setMethod] = useState(supportedMethods[0]?.type ?? 'totp')
	const [code, setCode] = useState('')
	const totpDevices = props.mfa.methods.filter((item) => item.type === 'totp')
	const [deviceId, setDeviceId] = useState(totpDevices[0]?.deviceId ?? '')
	const [emailSent, setEmailSent] = useState(false)
	const verify = useMutation({
		mutationFn: () => {
			if (method === 'totp') {
				return postAuthVerifyTotp({
					body: { mfaToken: props.mfa.mfaToken, deviceId, code }
				})
			}
			if (method === 'email_otp') {
				return postAuthVerifyTwoFactorOtp({
					body: { mfaToken: props.mfa.mfaToken, code }
				})
			}
			return postAuthVerifyBackupCode({
				body: { mfaToken: props.mfa.mfaToken, code: code.trim() }
			})
		},
		onError: () => setCode(''),
		onSuccess: props.onSuccess
	})
	const sendEmail = useMutation({
		mutationFn: () =>
			postAuthSendTwoFactorOtp({
				body: { mfaToken: props.mfa.mfaToken }
			}),
		onSuccess: () => setEmailSent(true)
	})
	const verifyPasskey = useMutation({
		mutationFn: async () => {
			const challenge = await postAuthGeneratePasskeyStepUpOptions({
				body: {}
			})
			const response = await getPasskeyAssertion(challenge.data.options)
			return postAuthVerifyTwoFactorPasskey({
				body: {
					mfaToken: props.mfa.mfaToken,
					passkeyToken: challenge.data.passkeyToken,
					response
				}
			})
		},
		onSuccess: props.onSuccess
	})
	const currentError = verify.error ?? sendEmail.error ?? verifyPasskey.error
	const pending =
		verify.isPending || sendEmail.isPending || verifyPasskey.isPending

	return (
		<div className='flex flex-col gap-4 text-center'>
			<ShieldCheck
				aria-hidden='true'
				className='mx-auto size-8 text-primary'
			/>
			<h1 className='font-semibold text-xl'>
				{t('common:auth.mfa_title')}
			</h1>
			<p className='text-muted-foreground text-sm'>
				{t('common:auth.mfa_description')}
			</p>
			<div className='grid grid-cols-2 gap-2'>
				{supportedMethods.map((item) => (
					<Button
						aria-pressed={method === item.type}
						key={item.type}
						onClick={() => {
							setMethod(item.type)
							setCode('')
							verify.reset()
						}}
						size='sm'
						type='button'
						variant={method === item.type ? 'default' : 'outline'}
					>
						{mfaMethodIcon(item.type)}
						{mfaMethodLabel(item.type, t)}
					</Button>
				))}
			</div>
			{currentError ? (
				<AuthErrorAlert
					error={currentError}
					onDismiss={() => {
						verify.reset()
						sendEmail.reset()
						verifyPasskey.reset()
					}}
				/>
			) : null}
			{method === 'totp' && totpDevices.length > 1 ? (
				<TwoFactorDevicePicker
					devices={totpDevices}
					onChange={setDeviceId}
					value={deviceId}
				/>
			) : null}
			<MfaMethodControl
				code={code}
				emailSent={emailSent}
				method={method}
				onCodeChange={setCode}
				onSendEmail={() => sendEmail.mutate()}
				onVerify={() => verify.mutate()}
				onVerifyPasskey={() => verifyPasskey.mutate()}
				pending={pending}
				sendEmailPending={sendEmail.isPending}
				t={t}
				verifyPasskeyPending={verifyPasskey.isPending}
				verifyPending={verify.isPending}
			/>
		</div>
	)
}

function MfaMethodControl(props: {
	code: string
	emailSent: boolean
	method: string
	onCodeChange: (code: string) => void
	onSendEmail: () => void
	onVerify: () => void
	onVerifyPasskey: () => void
	pending: boolean
	sendEmailPending: boolean
	verifyPasskeyPending: boolean
	verifyPending: boolean
	t: ReturnType<typeof useTranslation>['t']
}) {
	return (
		<>
			{props.method === 'email_otp' && !props.emailSent ? (
				<Button
					disabled={props.pending}
					onClick={props.onSendEmail}
					type='button'
				>
					{props.sendEmailPending ? (
						<Spinner />
					) : (
						props.t('common:auth.send_email_code')
					)}
				</Button>
			) : null}
			{props.method === 'passkey' ? (
				<Button
					disabled={props.pending}
					onClick={props.onVerifyPasskey}
					type='button'
				>
					{props.verifyPasskeyPending ? (
						<Spinner />
					) : (
						props.t('common:auth.use_passkey')
					)}
				</Button>
			) : null}
			{props.method === 'recovery_code' ? (
				<Input
					autoComplete='one-time-code'
					disabled={props.pending}
					onChange={(event) => props.onCodeChange(event.target.value)}
					placeholder={props.t('common:auth.backup_code')}
					value={props.code}
				/>
			) : null}
			{props.method !== 'passkey' &&
			(props.method !== 'email_otp' || props.emailSent) ? (
				<>
					{props.method !== 'recovery_code' ? (
						<OtpInput
							disabled={props.pending}
							onChange={props.onCodeChange}
							value={props.code}
						/>
					) : null}
					<Button
						className='w-full'
						disabled={
							props.pending ||
							(props.method !== 'recovery_code' &&
								props.code.length !== 6) ||
							!props.code.trim()
						}
						onClick={props.onVerify}
						type='button'
					>
						{props.verifyPending ? (
							<Spinner />
						) : (
							props.t('common:auth.verify')
						)}
					</Button>
				</>
			) : null}
		</>
	)
}

function mfaMethodIcon(method: string) {
	if (method === 'totp') {
		return <Smartphone />
	}
	if (method === 'passkey') {
		return <Fingerprint />
	}
	if (method === 'email_otp') {
		return <Mail />
	}
	return <KeyRound />
}

function mfaMethodLabel(
	method: string,
	t: ReturnType<typeof useTranslation>['t']
) {
	if (method === 'totp') {
		return t('common:auth.mfa_authenticator')
	}
	if (method === 'passkey') {
		return t('common:auth.mfa_passkey')
	}
	if (method === 'email_otp') {
		return t('common:auth.mfa_email')
	}
	return t('common:auth.mfa_recovery')
}
